import * as Api from '@ViewModels';
import {
	ICompany,
	IOperationResult,
	IOperationResultNoValue,
	QuickAddEntityViewModel,
	ResourceAutoCompleteViewModelType,
	VmUtils,
} from '@ViewModels';
import { css } from 'aphrodite';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useErrorMessages, useUserSession } from '../../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../../models/Logging';
import { getPrincipalInitials } from '../../../../../../../models/NameUtils';
import { canEditKeyFact } from '../../../../../../../models/UiUtils';
import { canMakeContactPrivate } from '../../../../../../../models/utils/contactUtils';
import { useGetExternalIntegrationsQuery } from '../../../../../../../queries/Integrations/useGetExternalIntegrations';
import {
	AutoCompleteSearchField,
	AutoCompleteSearchFieldAccessoryViewMode,
	IAutoCompleteSearchFieldComponent,
} from '../../../../../../components/autocomplete/AutoCompleteSearchField';
import { Avatar } from '@HoudiniUI/Avatar';
import { Button } from '../../../../../../components/Button';
import { styleSheet as editContactStyles } from '../../../../../../components/contacts/EditContactInfo/styles';
import { KitDropdown } from '../../../../../../components/contacts/KitDropdown';
import { DeprecatedTextArea } from '../../../../../../components/DeprecatedTextArea';
import { EditableEntityInfoBulletList } from '../../../../../../components/entities/EditableEntityInfoBulletList';
import { EditEntityInfoPhoneNumber } from '../../../../../../components/entities/EditEntityInfoPhoneNumber';
import { EntityInfoFieldLabel } from '../../../../../../components/entities/EntityInfoFieldLabel';
import { EntityInfoSection } from '../../../../../../components/entities/EntityInfoSection';
import { TagsListWithTagsEditor } from '../../../../../../components/entities/tags/TagsEditor';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner';
import { ClearFieldIcon } from '../../../../../../components/svgs/icons/ClearFieldIcon';
import { Input } from '../../../../../../components-v2/Input';
import { CountrySelector } from '../../../../../../components/ValueSelectors/CountrySelector';
import { INameValue } from '../../../../../../components/ValueSelectors/ValueSelector';
import { VisibilityDropdown } from '../../../../../../components/VisibilityDropdown';
import { baseStyleSheet } from '../../../../../../styles/styles';
import { styleSheet } from '../styles';
import { EditableCustomFields } from './EditableCustomFields';

type CustomFieldWithValue = Api.IFormField<string> & {
	value: string;
};

type ContactWithKitFrequency = Partial<Api.IContact> & {
	kitFrequency?: number;
	customFields?: CustomFieldWithValue[];
};

interface Props {
	contact: Api.IContact;
	onSave: (contactData: ContactWithKitFrequency) => void;
	onCancel: () => void;
	isLoading?: boolean;
	currentKitFrequency?: number;
}

const TWO_MB = 2097152;

export const EditableContactForm: React.FC<Props> = ({
	contact,
	onSave,
	onCancel,
	isLoading = false,
	currentKitFrequency = -1,
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logInput, logApiError } = useEventLogging('EditableContactForm');
	const inputRef = useRef<HTMLInputElement>(null);
	const companyAutoCompleteFieldRef = useRef<IAutoCompleteSearchFieldComponent>(null);
	const [quickAddEntityViewModel, setQuickAddEntityViewModel] = useState<QuickAddEntityViewModel | null>(null);
	const [companyNameFieldText, setCompanyNameFieldText] = useState<string>(contact.companyName || '');

	// Fetch v2 integrations
	const { data: _externalIntegrations, isSuccess: externalIntegrationsLoaded } = useGetExternalIntegrationsQuery({
		impersonationContext: undefined,
	});

	// Initialize form state with contact data
	const [formData, setFormData] = useState({
		firstName: contact.firstName || '',
		lastName: contact.lastName || '',
		jobTitle: contact.jobTitle || '',
		companyName: contact.companyName || '',
		companyId: contact.companyId || null,
		bio: contact.bio || '',
		webSite: contact.webSite || '',
		visibility: contact.visibility || VmUtils.getDefaultVisibility(userSession.user),
		primaryEmail: contact.primaryEmail?.value || '',
		profilePic: contact.profilePic || '',
	});

	const [keyFacts, setKeyFacts] = useState<Api.IKeyFact[]>(
		contact.keyFactsCollection ? [...contact.keyFactsCollection] : []
	);

	const [tags, setTags] = useState<Api.IAccountTag[]>(
		contact.tagsCollection
			? [...contact.tagsCollection]
			: contact.tags
				? contact.tags.map(tagName => ({ name: tagName }))
				: []
	);

	const [phoneNumbers, setPhoneNumbers] = useState<Api.IPhoneNumber[]>(
		contact.phoneNumbers ? [...contact.phoneNumbers] : []
	);

	const [address, setAddress] = useState<Api.IAddress | null>(contact.address ? { ...contact.address } : null);

	const [kitFrequency, setKitFrequency] = useState<number>(currentKitFrequency);

	const [customFields, setCustomFields] = useState<CustomFieldWithValue[]>([]);

	const contactViewModel = useMemo(() => new Api.ContactViewModel(userSession, contact), [userSession, contact]);

	const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setFormData(prev => ({
			...prev,
			[field]: e.target.value,
		}));
	};

	const handleVisibilityChange = (visibility: string) => {
		if (visibility === 'admin' && !canMakeContactPrivate(contact, userSession.user)) {
			errorMessages.push({
				messages: ['You do not have permission to make this contact private.'],
			});
			return;
		}
		setFormData(prev => ({
			...prev,
			visibility,
		}));
	};

	const handleKitFrequencyChange = (frequency: number) => {
		setKitFrequency(frequency);
	};

	const handleKeyFactChange = (keyFact: Api.IKeyFact, index: number) => {
		const newKeyFacts = [...keyFacts];
		newKeyFacts[index] = keyFact;
		setKeyFacts(newKeyFacts);
	};

	const addKeyFact = () => {
		const newKeyFact: Api.IKeyFact = {
			id: null,
			value: '',
		};
		setKeyFacts([...keyFacts, newKeyFact]);
	};

	const removeKeyFact = (index: number) => {
		const newKeyFacts = keyFacts.filter((_, i) => i !== index);
		setKeyFacts(newKeyFacts);
	};

	const handleAddTag = (tag: Api.IAccountTag) => {
		// Check if tag already exists
		if (!tags.find(t => t.name === tag.name)) {
			setTags([...tags, tag]);
		}
	};

	const handleRemoveTag = (tag: Api.IAccountTag) => {
		setTags(tags.filter(t => t.name !== tag.name));
	};

	const handlePhoneNumberValueChange = useCallback((index: number, value: string) => {
		setPhoneNumbers(prev => {
			const newPhoneNumbers = [...prev];
			newPhoneNumbers[index] = { ...newPhoneNumbers[index], value };
			return newPhoneNumbers;
		});
	}, []);

	const handlePhoneNumberLabelChange = useCallback((index: number, label: string) => {
		setPhoneNumbers(prev => {
			const newPhoneNumbers = [...prev];
			newPhoneNumbers[index] = { ...newPhoneNumbers[index], label };
			return newPhoneNumbers;
		});
	}, []);

	const addPhoneNumber = () => {
		const newPhoneNumber: Api.IPhoneNumber = {
			label: null,
			value: null,
		};
		setPhoneNumbers([...phoneNumbers, newPhoneNumber]);
	};

	const removePhoneNumber = (index: number) => {
		const newPhoneNumbers = phoneNumbers.filter((_, i) => i !== index);
		setPhoneNumbers(newPhoneNumbers);
	};

	const handleUploadClick = () => {
		inputRef.current?.click();
	};

	const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file: File = e.currentTarget.files?.[0];
		e.currentTarget.value = null;

		if (!file || file.size > TWO_MB) {
			errorMessages.push({
				messages: ['File size exceeded 2MB'],
			});
			return;
		}

		try {
			// Use the webServiceHelper to upload the profile picture
			const uploadFormData = new FormData();
			uploadFormData.append('file', file);

			const opResult: Api.IOperationResult<Api.IContact> = await userSession.webServiceHelper.callWebServiceAsync(
				`contact/${contact.id}/profilePic`,
				'PUT',
				uploadFormData
			);

			if (opResult.success) {
				setFormData(prev => ({
					...prev,
					profilePic: opResult.value.profilePic,
				}));
			} else {
				throw opResult;
			}
		} catch (error) {
			errorMessages.push({
				messages: ['There was an issue uploading the image. Please try again later.'],
			});
			console.error('UploadContactPic-Error', error);
		}
	};

	const onDeletePicClicked = async () => {
		try {
			// Use the webServiceHelper to update the contact and remove the profile picture
			const contactModel = { ...contact };
			contactModel.profilePic = '';

			const opResult: Api.IOperationResult<Api.IContact> = await userSession.webServiceHelper.callWebServiceAsync(
				`contact/${contact.id}`,
				'PUT',
				contactModel
			);

			if (opResult.success) {
				logInput('DeleteProfilePic', 'Click', { id: contact.id });
				setFormData(prev => ({
					...prev,
					profilePic: '',
				}));
			} else {
				throw opResult;
			}
		} catch (error) {
			errorMessages.push({
				messages: ['There was an issue removing the image. Please try again later.'],
			});
			console.error('DeleteProfilePic-Error', error);
		}
	};

	const renderKeyFactEditor = (keyFact: Api.IKeyFact, index: number) => {
		return (
			<Input
				key={index}
				autoComplete='off'
				id={`key-fact-${index}`}
				onChange={e => {
					handleKeyFactChange({ ...keyFact, value: e.target.value }, index);
				}}
				placeholder='Key fact'
				type='text'
				value={keyFact.value || ''}
			/>
		);
	};

	const canRemoveKeyFact = useCallback(
		(keyFact: Api.IKeyFact) => {
			// Prevent removal until external integrations are loaded to avoid race condition
			// where integration-sourced key facts might appear temporarily editable
			if (!externalIntegrationsLoaded) {
				return false;
			}

			return canEditKeyFact(keyFact, userSession.account.integrations, _externalIntegrations);
		},
		[userSession.account.integrations, _externalIntegrations, externalIntegrationsLoaded]
	);

	const renderPhoneNumberEditor = useCallback(
		(phoneNumber: Api.IPhoneNumber, index: number) => {
			return (
				<EditEntityInfoPhoneNumber
					key={index}
					rootElementType='none'
					label={phoneNumber.label}
					onLabelChanged={(label: string) => handlePhoneNumberLabelChange(index, label)}
					onValueChanged={(value: string) => handlePhoneNumberValueChange(index, value)}
					value={phoneNumber.value}
					inputStyles={[styleSheet.editInput]}
				/>
			);
		},
		[handlePhoneNumberLabelChange, handlePhoneNumberValueChange]
	);

	const createCompanyWithName = useCallback(
		async (companyName: string): Promise<IOperationResult<ICompany>> => {
			const trimmedCompanyName = (companyName || '').trim();
			if (!trimmedCompanyName) {
				throw new Error('Company name is required');
			}

			const quickAddViewModel = new QuickAddEntityViewModel(userSession);
			quickAddViewModel.companyName = trimmedCompanyName;
			quickAddViewModel.entityType = 'company';
			setQuickAddEntityViewModel(quickAddViewModel);

			try {
				logInput('CreateCompany', 'Click');
				const opResult = await quickAddViewModel.save();

				if (opResult.success) {
					// Update form data with the created company
					setFormData(prev => ({
						...prev,
						companyName: opResult.value.companyName,
						companyId: opResult.value.id,
					}));

					// Update the autocomplete field
					if (companyAutoCompleteFieldRef.current) {
						companyAutoCompleteFieldRef.current.setSearchQuery(opResult.value.companyName);
					}

					setQuickAddEntityViewModel(null);
					return opResult;
				} else {
					throw opResult;
				}
			} catch (error) {
				logApiError('CreateCompany-Error', error);
				errorMessages.pushApiError(error as IOperationResultNoValue);
				setQuickAddEntityViewModel(null);
				throw error;
			}
		},
		[userSession, logInput, logApiError, errorMessages]
	);

	const onCompanyChanged = useCallback((company: ICompany) => {
		setFormData(prev => ({
			...prev,
			companyName: company.companyName,
			companyId: company.id,
		}));
		setCompanyNameFieldText(company.companyName);
	}, []);

	const clearCompany = useCallback(() => {
		if (companyAutoCompleteFieldRef.current) {
			companyAutoCompleteFieldRef.current.clearInput();
		}
		setFormData(prev => ({
			...prev,
			companyName: '',
			companyId: null,
		}));
		setCompanyNameFieldText('');
	}, []);

	const onCompanyAutoCompleteFieldBlur = useCallback(() => {
		if (formData.companyName !== companyNameFieldText) {
			// Clear company ID and set as new company name
			setFormData(prev => ({
				...prev,
				companyName: companyNameFieldText,
				companyId: null,
			}));
		}
	}, [formData.companyName, companyNameFieldText]);

	const onCompanyFieldChanged = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setCompanyNameFieldText(e.target.value);
			if (!e.target.value) {
				clearCompany();
			}
		},
		[clearCompany]
	);

	const handleSave = async () => {
		// Validate required fields
		const hasEmailAddress = formData.primaryEmail.trim() !== '';
		const hasFullName = formData.firstName.trim() !== '' && formData.lastName.trim() !== '';

		if (!hasEmailAddress && !hasFullName) {
			errorMessages.push({
				messages: ['You must either enter an email address or the first/last name for a contact.'],
			});
			return;
		}

		// Build contact data
		let contactData: Partial<Api.IContact> = {
			...contact,
			company: formData.companyId ? { id: formData.companyId } : null,
			firstName: formData.firstName || null,
			lastName: formData.lastName || null,
			jobTitle: formData.jobTitle || null,
			companyName: formData.companyName || null,
			companyId: formData.companyId || null,
			bio: formData.bio || null,
			webSite: formData.webSite || null,
			visibility: formData.visibility,
			profilePic: formData.profilePic || null,
			keyFactsCollection: keyFacts.filter(kf => kf.value && kf.value.trim() !== ''),
			tags: tags.map(tag => tag.name).filter(name => name && name.trim() !== ''),
			phoneNumbers: phoneNumbers.filter(pn => pn.value && pn.value.trim() !== ''),
			address,
		};

		// Check if we need to create the company
		if (!!contactData.companyName && !contactData.companyId) {
			try {
				const opResult = await createCompanyWithName(contactData.companyName);
				contactData = {
					...contactData,
					companyName: opResult.value.companyName,
					companyId: opResult.value.id,
					company: { id: opResult.value.id },
				};
			} catch {
				// Error already handled by createCompanyWithName
				return;
			}
		}

		// Handle email update
		if (formData.primaryEmail) {
			contactData.emailAddresses = [
				{ value: formData.primaryEmail },
				...(contact.emailAddresses || []).slice(1), // Keep other emails
			];
		}

		// Include keep in touch frequency for later processing
		const contactDataWithKitFrequency: ContactWithKitFrequency = {
			...contactData,
			kitFrequency,
			customFields,
		};

		// Clean up empty values
		Object.keys(contactDataWithKitFrequency).forEach(key => {
			const typedKey = key as keyof typeof contactDataWithKitFrequency;
			if (contactDataWithKitFrequency[typedKey] === '') {
				(contactDataWithKitFrequency as any)[typedKey] = null;
			}
		});

		logInput('Save', 'Click');
		onSave(contactDataWithKitFrequency);
	};

	const renderSaveCancelButtons = () => {
		return (
			<div className={css(styleSheet.stickyFooter)}>
				<Button disabled={isLoading} onClick={handleSave} type='button'>
					{isLoading ? <LoadingSpinner type='tiny' /> : 'Save'}
				</Button>
				<Button disabled={isLoading} onClick={onCancel} type='button' kind='reverse'>
					Cancel
				</Button>
			</div>
		);
	};

	return (
		<div className={css(styleSheet.stickyContainer)}>
			<div className={css(styleSheet.stickyScrollableContent)}>
				<div className={css(editContactStyles.container, styleSheet.editContainerContent)}>
					<div className={css(editContactStyles.title, styleSheet.editTitle)}>Edit Contact</div>

					{/* Avatar Section */}
					<div className={css(editContactStyles.header, styleSheet.editHeader)}>
						<div className={css(editContactStyles.headerAvatarContainer, styleSheet.editHeaderAvatarContainer)}>
							<Avatar
								styles={[styleSheet.profileInfoAvatar]}
								fallbackStyles={[styleSheet.profileInfoAvatar]}
								fallbackText={getPrincipalInitials(contact)}
								imgSrc={formData.profilePic}
							/>
							<input
								id='profileUploadInput'
								accept='image/png, image/jpeg, image/jpg'
								className=''
								onChange={onFileChange}
								type='file'
								ref={inputRef}
								style={{ position: 'absolute', visibility: 'hidden', zIndex: -1 }}
							/>
							{formData.profilePic ? (
								<Button kind='link' onClick={onDeletePicClicked}>
									<span>Delete Profile Image</span>
								</Button>
							) : (
								<div>
									<p className={css(editContactStyles.profilePlaceHolderBlurb)}>JPG or PNG, max size 2MB</p>
									<button type='button' className={css(baseStyleSheet.ctaButtonSmall)} onClick={handleUploadClick}>
										<span>Upload</span>
									</button>
								</div>
							)}
						</div>
					</div>

					{/* Name Fields */}
					<div className={css(editContactStyles.nameFields, editContactStyles.fieldGroup)}>
						<Input
							autoComplete='off'
							id='edit-contact-firstName-input'
							onChange={handleInputChange('firstName')}
							placeholder='First Name'
							type='text'
							value={formData.firstName}
						/>
						<Input
							autoComplete='off'
							id='edit-contact-lastName-input'
							onChange={handleInputChange('lastName')}
							placeholder='Last Name'
							type='text'
							value={formData.lastName}
						/>
					</div>

					{/* Title / Company */}
					<EntityInfoSection title='Title / Company'>
						<div className={css(editContactStyles.fieldGroup)}>
							<Input
								autoComplete='off'
								id='edit-contact-jobTitle-input'
								onChange={handleInputChange('jobTitle')}
								placeholder='Title'
								type='text'
								value={formData.jobTitle}
							/>
							<AutoCompleteSearchField
								className={css(styleSheet.editInput)}
								initialSearchQuery={contact.companyName ? contact.companyName : undefined}
								inputId='edit-contact-company-input'
								inputProps={{
									disabled: !!quickAddEntityViewModel && !!quickAddEntityViewModel.isBusy,
									onBlur: onCompanyAutoCompleteFieldBlur,
									onChange: onCompanyFieldChanged,
								}}
								onInnerRef={(ref?: IAutoCompleteSearchFieldComponent) => {
									if (ref) {
										companyAutoCompleteFieldRef.current = ref;
									}
								}}
								onItemSelected={onCompanyChanged}
								placeholder='Company'
								rightAccessory={
									formData.companyName && (
										<button onMouseDown={clearCompany}>
											<ClearFieldIcon />
										</button>
									)
								}
								rightAccessoryViewMode={AutoCompleteSearchFieldAccessoryViewMode.WhileEditing}
								type={ResourceAutoCompleteViewModelType.Company}
							/>
						</div>
					</EntityInfoSection>

					{/* Visibility */}
					<EntityInfoSection title='Visibility'>
						<VisibilityDropdown onVisibilityChanged={handleVisibilityChange} visibility={formData.visibility} />
					</EntityInfoSection>

					{/* Reminders / Key Facts / Tags */}
					<EntityInfoSection title='Reminders / Key Facts / Tags'>
						<div className={css(editContactStyles.fieldGroup)}>
							<div>
								<EntityInfoFieldLabel title={`Keep in touch${formData.firstName ? ` ${formData.firstName}` : ''}`} />
								<KitDropdown
									className={css(editContactStyles.kitDropdown)}
									keepInTouchFrequency={kitFrequency}
									onFrequencyChanged={handleKitFrequencyChange}
								/>
							</div>

							{/* Custom Fields */}
							<EditableCustomFields contact={contact} onCustomFieldsChange={setCustomFields} />

							<div>
								<EntityInfoFieldLabel title='Key facts' />
								<div className={css(editContactStyles.fieldGroup)}>
									<EditableEntityInfoBulletList
										addButtonContent={<span>Add key fact</span>}
										items={keyFacts}
										itemValueKeyPath='value'
										onAddButtonClicked={addKeyFact}
										onRenderItem={renderKeyFactEditor}
										onRemoveButtonClicked={(_, index) => removeKeyFact(index)}
										baseItemKey='key-fact'
										canRemoveItem={canRemoveKeyFact}
									/>
								</div>
							</div>
							<div>
								<EntityInfoFieldLabel title='Tags' />
								<TagsListWithTagsEditor
									tagType={Api.ResourceAutoCompleteViewModelType.AccountTag}
									entity={contactViewModel}
									onRequestAddTag={handleAddTag}
									onRequestRemoveTag={handleRemoveTag}
									tags={tags}
								/>
							</div>
						</div>
					</EntityInfoSection>

					{/* Contact Information */}
					<EntityInfoSection title='Contact Information'>
						<div className={css(editContactStyles.fieldGroup)}>
							<div>
								<EntityInfoFieldLabel title='Primary Email' />
								<Input
									autoComplete='off'
									id='edit-contact-primaryEmail-input'
									onChange={handleInputChange('primaryEmail')}
									placeholder='Primary Email'
									type='email'
									value={formData.primaryEmail}
								/>
							</div>
							<div>
								<EntityInfoFieldLabel title='Phone Number' />
								<div className={css(editContactStyles.fieldGroup)}>
									<EditableEntityInfoBulletList
										addButtonContent={<span>Add phone number</span>}
										items={phoneNumbers}
										itemValueKeyPath='value'
										onAddButtonClicked={addPhoneNumber}
										onRemoveButtonClicked={(_, index) => removePhoneNumber(index)}
										onRenderItem={renderPhoneNumberEditor}
										baseItemKey='phone-number'
									/>
								</div>
							</div>
							<div>
								<EntityInfoFieldLabel title='Website' />
								<Input
									autoComplete='off'
									id='edit-contact-webSite-input'
									onChange={handleInputChange('webSite')}
									placeholder='Website'
									type='url'
									value={formData.webSite}
								/>
							</div>

							<div>
								<EntityInfoFieldLabel title='Address Line 1' />
								<Input
									autoComplete='off'
									id='edit-contact-address1-input'
									onChange={e => {
										setAddress(prev => ({
											...prev,
											address1: e.target.value,
										}));
									}}
									placeholder='Address Line 1'
									type='text'
									value={address?.address1}
								/>
							</div>

							<div>
								<EntityInfoFieldLabel title='Address Line 2' />
								<Input
									autoComplete='off'
									id='edit-contact-address2-input'
									onChange={e => {
										setAddress(prev => ({
											...prev,
											address2: e.target.value,
										}));
									}}
									placeholder='Address Line 2'
									type='text'
									value={address?.address2}
								/>
							</div>

							<div className={css(styleSheet.addressFieldsContainer)}>
								<div className={css(styleSheet.addressField)}>
									<EntityInfoFieldLabel title='City' />
									<Input
										autoComplete='off'
										id='edit-contact-city-input'
										onChange={e => {
											setAddress(prev => ({
												...prev,
												city: e.target.value,
											}));
										}}
										placeholder='City'
										type='text'
										value={address?.city}
									/>
								</div>

								<div className={css(styleSheet.addressField)}>
									<EntityInfoFieldLabel title='State/Province' />
									<Input
										autoComplete='off'
										id='edit-contact-stateProvince-input'
										onChange={e => {
											setAddress(prev => ({
												...prev,
												stateProvince: e.target.value,
											}));
										}}
										placeholder='State/Province'
										type='text'
										value={address?.stateProvince}
									/>
								</div>
							</div>

							<div className={css(styleSheet.addressFieldsContainer)}>
								<div className={css(styleSheet.addressField)}>
									<EntityInfoFieldLabel title='Zip/Postal Code' />
									<Input
										autoComplete='off'
										id='edit-contact-postalCode-input'
										onChange={e => {
											setAddress(prev => ({
												...prev,
												postalCode: e.target.value,
											}));
										}}
										placeholder='Zip/Postal Code'
										type='text'
										value={address?.postalCode}
									/>
								</div>

								<div className={css(styleSheet.addressField)}>
									<EntityInfoFieldLabel title='Country' />
									<CountrySelector
										className={css(styleSheet.editInput)}
										onSelectionChanged={(country: INameValue) => {
											const value = country.value !== 'None' ? country.value : null;
											setAddress(prev => ({
												...prev,
												country: value,
											}));
										}}
										selectedValue={address?.country}
									/>
								</div>
							</div>

							{/* <EditableEntityAddress address={address} onAddressChanged={handleAddressChange} /> */}
							<div>
								<EntityInfoFieldLabel title='Bio' />
								<DeprecatedTextArea
									inputClassName={css(editContactStyles.bioTextArea, styleSheet.editInput)}
									inputId='edit-contact-bio-input'
									onChange={handleInputChange('bio')}
									value={formData.bio}
								/>
							</div>
						</div>
					</EntityInfoSection>

					{isLoading && <LoadingSpinner className='absolute-center' type='large' />}
				</div>
			</div>

			{renderSaveCancelButtons()}
		</div>
	);
};
